import { MapPin, Phone, Mail, Clock, Car, Navigation } from "lucide-react";
import { BRAND, IMAGES } from "@/lib/constants";

export default function Locations() {
  return (
    <div data-testid="locations-page">
      <section className="py-14 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="max-w-3xl animate-fade-up">
            <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold">
              Our Location
            </p>
            <h1 className="mt-4 font-display text-[32px] leading-[1.1] sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white">
              Bethesda · <span className="text-[#28C0D8]">Maryland</span>
            </h1>
            <p className="mt-6 text-[17px] text-[#94A3B8] max-w-2xl">
              Find us on Maple Ave — steps from downtown Bethesda, with easy
              access for residents across Montgomery County and the DMV.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-20 md:pb-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-4 md:gap-8">
            <div className="col-span-12 lg:col-span-7 rounded-3xl overflow-hidden border border-white/10 min-h-[420px] lg:min-h-[560px]">
              <iframe
                title="MD Supply Wellness map"
                src={BRAND.mapEmbed}
                width="100%"
                height="100%"
                style={{
                  border: 0,
                  filter: "invert(0.92) hue-rotate(180deg) saturate(0.6)",
                }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            <div className="col-span-12 lg:col-span-5 flex flex-col gap-6">
              <div className="rounded-3xl border border-white/10 bg-[#13161C] p-8">
                <h2 className="font-display text-2xl font-semibold text-white tracking-tight">
                  MD Supply Wellness
                </h2>
                <ul className="mt-6 space-y-5 text-[15px]">
                  <li className="flex gap-4 text-white/85">
                    <MapPin className="h-5 w-5 text-[#28C0D8] shrink-0 mt-0.5" />
                    <span>
                      {BRAND.address.line1}
                      <br />
                      {BRAND.address.line2}
                    </span>
                  </li>
                  <li>
                    <a
                      href={`tel:${BRAND.phoneRaw}`}
                      className="flex gap-4 text-white/85 hover:text-[#28C0D8]"
                      data-testid="location-phone"
                    >
                      <Phone className="h-5 w-5 text-[#28C0D8] shrink-0 mt-0.5" />
                      {BRAND.phone}
                    </a>
                  </li>
                  <li>
                    <a
                      href={`mailto:${BRAND.email}`}
                      className="flex gap-4 text-white/85 hover:text-[#28C0D8] break-all"
                    >
                      <Mail className="h-5 w-5 text-[#28C0D8] shrink-0 mt-0.5" />
                      {BRAND.email}
                    </a>
                  </li>
                </ul>

                <div className="mt-8 flex flex-wrap gap-3">
                  <a
                    href={BRAND.mapsLink}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-teal-glow inline-flex items-center gap-2 rounded-full bg-[#28C0D8] px-5 py-2.5 text-sm font-semibold text-[#0A0B0E] hover:bg-[#1FA8BC]"
                    data-testid="location-directions-btn"
                  >
                    <Navigation className="h-4 w-4" /> Get Directions
                  </a>
                  <a
                    href={`tel:${BRAND.phoneRaw}`}
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm text-white hover:bg-white/5"
                  >
                    <Phone className="h-4 w-4" /> Call us
                  </a>
                </div>
              </div>

              <div className="rounded-3xl border border-white/10 bg-[#13161C] p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="h-5 w-5 text-[#28C0D8]" />
                  <h3 className="font-display text-lg font-semibold text-white">
                    Hours
                  </h3>
                </div>
                <ul className="space-y-2 text-sm">
                  {BRAND.hours.map((h) => (
                    <li
                      key={h.day}
                      className="flex justify-between gap-4 text-white/80 border-b border-white/5 pb-2 last:border-0"
                    >
                      <span>{h.day}</span>
                      <span className="text-white">{h.time}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-5 text-xs text-[#64748B]">
                  Hours are subject to change at launch. Join our waitlist for
                  confirmed opening hours.
                </p>
              </div>

              <div className="rounded-3xl border border-white/10 bg-[#13161C] p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Car className="h-5 w-5 text-[#28C0D8]" />
                  <h3 className="font-display text-lg font-semibold text-white">
                    Getting here
                  </h3>
                </div>
                <p className="text-sm text-[#94A3B8] leading-relaxed">
                  Conveniently located on Maple Ave in downtown Bethesda —
                  accessible via the Bethesda Metro (Red Line), MARC, and major
                  arteries including Wisconsin Ave & Old Georgetown Rd. Street
                  and nearby garage parking available.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-white/5 bg-[#0A0B0E]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 py-20">
          <div className="relative rounded-3xl overflow-hidden border border-white/10 min-h-[280px]">
            <img
              src={IMAGES.interior}
              alt="Premium dispensary interior inspiration"
              className="absolute inset-0 w-full h-full object-cover opacity-70"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0B0E] via-[#0A0B0E]/70 to-transparent" />
            <div className="relative p-10 md:p-16 max-w-xl">
              <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold">
                The experience
              </p>
              <h3 className="mt-4 font-display text-3xl md:text-4xl font-semibold text-white tracking-tight">
                A calm, clinic-grade retail space.
              </h3>
              <p className="mt-4 text-[15px] text-[#CBD5E1]">
                Designed to feel as thoughtful as a modern wellness clinic —
                with room to ask questions, explore, and take your time.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
